import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
    useMemo
} from 'react';
import mapboxgl from 'mapbox-gl';
import { useResizeDetector } from 'react-resize-detector';
import html2canvas from 'html2canvas';
import './Geography.scss';

mapboxgl.accessToken =
    'pk.eyJ1Ijoib3BzLWF0LWdldHRvb2xiZWx0LWRvdC1jb20iLCJhIjoiY2tqYWVlazdqN2FoajJ2cGRyaDM0b2w5dSJ9.0ghFxBToWVY2tiKhPDrs6w';

// State abbreviations → FIPS
const USPS_TO_FIPS = {
    AL:'01', AK:'02', AZ:'04', AR:'05', CA:'06', CO:'08', CT:'09', DE:'10', FL:'12',
    GA:'13', HI:'15', ID:'16', IL:'17', IN:'18', IA:'19', KS:'20', KY:'21', LA:'22',
    ME:'23', MD:'24', MA:'25', MI:'26', MN:'27', MS:'28', MO:'29', MT:'30', NE:'31',
    NV:'32', NH:'33', NJ:'34', NM:'35', NY:'36', NC:'37', ND:'38', OH:'39', OK:'40',
    OR:'41', PA:'42', RI:'44', SC:'45', SD:'46', TN:'47', TX:'48', UT:'49', VT:'50',
    VA:'51', WA:'53', WV:'54', WI:'55', WY:'56', DC:'11', PR:'72'
};

const STATES_URL =
    'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';

const ProspectOpportunity = forwardRef(function ProspectOpportunity(props, ref) {
    const regionData = props?.regionData ?? [];
    const [map, setMap] = useState(null);
    const [statesGeo, setStatesGeo] = useState(null); // keep original geojson to re-join
    const mapContainer = useRef(null);

    // Hook: resize observer for the map container
    useResizeDetector({
        handleWidth: true,
        handleHeight: true,
        targetRef: mapContainer,
        onResize: () => map?.resize()
    });

    // Build FIPS -> count lookup from props
    const regionCounts = useMemo(() => {
        const out = {};
        for (const r of regionData) {
            // tolerate different casings / types
            const abbr = String(r.companyAddressRegion ?? r.COMPANY_ADDRESS_REGION ?? '').trim().toUpperCase();
            const cnt = Number(r.count ?? 0) || 0;
            const fips = USPS_TO_FIPS[abbr];
            if (fips) out[fips] = cnt;
        }
        return out;
    }, [regionData]);

    // Min/Max safe defaults
    const { min, max } = useMemo(() => {
        const vals = Object.values(regionCounts);
        if (!vals.length) return { min: 0, max: 1 };     // avoids NaN in color stops
        const mn = Math.min(...vals);
        const mx = Math.max(...vals);
        // if all equal (including all zero), widen slightly so interpolate still works
        return mn === mx ? { min: mn, max: mn + 1 } : { min: mn, max: mx };
    }, [regionCounts]);

    // Init map once
    useEffect(() => {
        const mapInstance = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/light-v10',
            center: [-98.2883, 38.5],
            zoom: 3.2,
            preserveDrawingBuffer: true
        });

        mapInstance.on('load', async () => {
            setMap(mapInstance);

            // fetch states geojson once
            const resp = await fetch(STATES_URL);
            const geo = await resp.json();
            setStatesGeo(geo);

            // attach an initial count (0)
            geo.features.forEach(f => { f.properties.count = 0; });

            mapInstance.addSource('us-states', { type: 'geojson', data: geo });

            mapInstance.addLayer({
                id: 'state-fills',
                type: 'fill',
                source: 'us-states',
                paint: {
                    // will update these stops whenever props change
                    'fill-color': [
                        'interpolate',
                        ['linear'],
                        ['get', 'count'],
                        0, '#fce8e0',
                        1, '#fce8e0'
                    ],
                    'fill-opacity': 0.9
                }
            });

            mapInstance.addLayer({
                id: 'state-borders',
                type: 'line',
                source: 'us-states',
                paint: { 'line-color': '#333', 'line-width': 1 }
            });

            mapInstance.addLayer({
                id: 'state-labels',
                type: 'symbol',
                source: 'us-states',
                layout: {
                    'text-field': [
                        'case',
                        ['>=', ['get', 'count'], 1000],
                        ['concat',
                            ['to-string', ['round', ['/', ['get', 'count'], 1000]]],
                            'K'
                        ],
                        ['to-string', ['get', 'count']]
                    ],
                    'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                    'text-size': 14,
                    'text-allow-overlap': false,
                    'text-padding': 0,
                    'symbol-avoid-edges': true,
                    'symbol-placement': 'point',
                    'text-optional': false
                },
                paint: {
                    'text-color': '#333',
                    'text-halo-color': '#fff',
                    'text-halo-width': 2
                }
            });
        });

        return () => { try { mapInstance.remove(); } catch {} };
    }, []);

    // Re-join data + repaint when regionData changes
    useEffect(() => {
        if (!map || !statesGeo) return;

        // create a fresh copy so we can setData
        const updated = { ...statesGeo, features: statesGeo.features.map(f => {
                const ff = { ...f, properties: { ...f.properties } };
                const fips = ff.id;         // FIPS code in this dataset
                ff.properties.count = regionCounts[fips] || 0;
                return ff;
            })};

        const src = map.getSource('us-states');
        if (src) src.setData(updated);

        // Update color stops with new min/max
        if (map.getLayer('state-fills')) {
            map.setPaintProperty(
                'state-fills',
                'fill-color',
                [
                    'interpolate',
                    ['linear'],
                    ['get', 'count'],
                    min, '#fce8e0',
                    max, '#ee5a38'
                ]
            );
        }
    }, [map, statesGeo, regionCounts, min, max]);

    // Export PNG for PDF
    useImperativeHandle(ref, () => ({
        getMapImageData: async () => {
            if (!map) return null;
            try {
                await new Promise(res => map.once('idle', res));
                return map.getCanvas().toDataURL('image/png');
            } catch {
                try {
                    const el = map.getContainer();
                    const cv = await html2canvas(el, { useCORS: true });
                    return cv.toDataURL('image/png');
                } catch {
                    return null;
                }
            }
        }
    }));

    return (
        <div ref={mapContainer} className="prospectOpportunityMap" id="prospectOpportunityMapID" />
    );
});

export default ProspectOpportunity;
